import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Shield,
  Database,
  Share2,
  Clock,
  Mail,
  UserX,
  Lock,
  Baby,
  Globe,
  AlertTriangle,
  FileText,
  Facebook,
} from 'lucide-react';

const EFFECTIVE_DATE = 'February 21, 2026';
const CONTACT_EMAIL = 'privacy@m4manna.com';
const SUPPORT_EMAIL = 'contact@m4manna.com';

const Section = ({ icon: Icon, title, id, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-10 w-10 rounded-full bg-[#FFE500]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-[#FFE500]" />
      </div>
      <h2 className="text-2xl font-extrabold text-white">{title}</h2>
    </div>
    <div className="space-y-3 text-[#A0A0A0] leading-relaxed pl-[52px]">{children}</div>
  </section>
);

const Privacy = () => {
  const toc = [
    { id: 'what-we-collect', label: '1. Information We Collect' },
    { id: 'how-we-collect', label: '2. How We Collect It' },
    { id: 'why-we-collect', label: '3. Why We Collect It' },
    { id: 'how-we-use', label: '4. How We Use & Store It' },
    { id: 'sharing', label: '5. Sharing With Third Parties' },
    { id: 'facebook', label: '6. Facebook Platform Data' },
    { id: 'cookies', label: '7. Cookies & Tracking' },
    { id: 'retention', label: '8. Data Retention' },
    { id: 'your-rights', label: '9. Your Rights & Choices' },
    { id: 'delete-data', label: '10. How to Delete Your Data' },
    { id: 'children', label: '11. Children\'s Privacy' },
    { id: 'international', label: '12. International Transfers' },
    { id: 'changes', label: '13. Changes to This Policy' },
    { id: 'contact', label: '14. Contact Us' },
  ];

  return (
    <Layout>
      <div className="py-10 md:py-14 px-4 md:px-8 pb-40" data-testid="privacy-page">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 bg-[#FFE500]/10 border border-[#FFE500]/20 px-4 py-2 rounded-full mb-6">
              <Shield className="h-4 w-4 text-[#FFE500]" />
              <span className="text-sm text-[#FFE500] font-semibold uppercase tracking-wider">
                Legal
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">
              Privacy <span className="text-[#FFE500]">Policy</span>
            </h1>
            <p className="text-lg text-[#A0A0A0] max-w-3xl">
              Your privacy matters. This policy explains what we collect, how we use it, who we share it with, and the
              rights you have over your data on M4manna.
            </p>
            <p className="text-sm text-[#666] mt-4">
              Effective date: <span className="text-white font-semibold">{EFFECTIVE_DATE}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10">
            {/* Content */}
            <div className="space-y-10">
              {/* Intro */}
              <div className="bg-[#16181D] border border-[#1E2028] rounded-2xl p-6">
                <p className="text-[#A0A0A0] leading-relaxed">
                  M4manna (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the website{' '}
                  <a href="https://m4manna.com" className="text-[#FFE500] hover:underline">m4manna.com</a>{' '}
                  and related services (collectively, the &quot;Service&quot;) — a platform that connects gospel and worship
                  artists with listeners through music streaming, downloads, bio links, and mentorship bookings. By
                  using the Service, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>

              {/* 1. What We Collect */}
              <Section icon={Database} title="1. Information We Collect" id="what-we-collect">
                <p>We collect only what we need to run and improve the Service.</p>

                <h3 className="text-white font-bold mt-5 mb-2">a) Information you give us</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Account:</strong> full name, username, email address, hashed password, role (fan / artist / mentor), optional profile image and bio.</li>
                  <li><strong className="text-white">Content:</strong> songs, audio files, cover images, lyrics, YouTube links, bio-link entries, and any other content you upload.</li>
                  <li><strong className="text-white">Bookings &amp; messages:</strong> session preferences, booking dates and times, and messages you exchange with mentors or artists.</li>
                  <li><strong className="text-white">Payment information:</strong> processed securely through Razorpay. We receive transaction IDs, status, and amounts — <em>we never see or store your full card number, UPI PIN, CVV, or bank credentials.</em></li>
                  <li><strong className="text-white">Communications:</strong> emails, support tickets, or feedback you send us.</li>
                </ul>

                <h3 className="text-white font-bold mt-5 mb-2">b) Information collected automatically</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Usage data:</strong> song plays, likes, playlist votes, pages visited, time spent, actions taken.</li>
                  <li><strong className="text-white">Device &amp; log data:</strong> IP address, browser type, operating system, device identifiers, referral URLs, access timestamps.</li>
                  <li><strong className="text-white">Cookies &amp; local storage:</strong> session tokens (JWT), preferences, authentication state (see Section 7).</li>
                </ul>

                <h3 className="text-white font-bold mt-5 mb-2">c) Information from third parties</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>If you sign in with a social provider (e.g. Facebook, Google), we receive basic profile info permitted by your choices on that platform (name, email, public profile picture, platform user ID).</li>
                  <li>If you share your M4manna profile on social networks, those networks may send us referral and attribution data.</li>
                </ul>
              </Section>

              {/* 2. How We Collect */}
              <Section icon={Globe} title="2. How We Collect It" id="how-we-collect">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Directly from you when you sign up, upload, book a session, make a payment, or contact us.</li>
                  <li>Automatically as you interact with the Service, via standard web technologies (cookies, local storage, server logs).</li>
                  <li>From third-party providers you choose to connect (e.g. Facebook Login, YouTube embeds, payment gateways).</li>
                </ul>
              </Section>

              {/* 3. Why */}
              <Section icon={FileText} title="3. Why We Collect It" id="why-we-collect">
                <p>Each category of data serves a specific purpose:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Provide the Service:</strong> create your account, publish your profile, stream music, process bookings and payments.</li>
                  <li><strong className="text-white">Personalize:</strong> show relevant artists, recommend songs, remember preferences.</li>
                  <li><strong className="text-white">Communicate:</strong> send booking confirmations, verification emails, service updates, and (with consent) occasional product announcements.</li>
                  <li><strong className="text-white">Security &amp; abuse prevention:</strong> detect fraud, protect against unauthorized access, enforce our Terms.</li>
                  <li><strong className="text-white">Legal compliance:</strong> respond to lawful requests and meet tax, accounting and other regulatory obligations.</li>
                  <li><strong className="text-white">Improve the Service:</strong> understand what works, fix bugs, build new features.</li>
                </ul>
              </Section>

              {/* 4. How we use & store */}
              <Section icon={Lock} title="4. How We Use & Store It" id="how-we-use">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Account data is stored in our MongoDB database.</li>
                  <li>Passwords are stored as <strong className="text-white">bcrypt hashes</strong> — never in plain text.</li>
                  <li>Uploaded media (audio, images) is stored securely on our FTP media server.</li>
                  <li>All traffic to the Service is served over <strong className="text-white">HTTPS/TLS</strong>.</li>
                  <li>Access to our production systems is restricted to authorized personnel with multi-factor authentication.</li>
                  <li>Payments run through Razorpay (PCI-DSS compliant); we do not store sensitive card data on our servers.</li>
                </ul>
                <p className="mt-3 text-sm bg-[#1E2028] p-3 rounded-xl border-l-4 border-[#FFE500]">
                  <AlertTriangle className="h-4 w-4 inline mr-2 text-[#FFE500]" />
                  No internet-connected system is 100% secure. While we work hard to protect your data, we cannot
                  guarantee absolute security. You can help by using a strong unique password.
                </p>
              </Section>

              {/* 5. Sharing */}
              <Section icon={Share2} title="5. Sharing With Third Parties" id="sharing">
                <p>
                  <strong className="text-white">We do not sell your personal data.</strong> We share information only in
                  these limited cases:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Service providers (sub-processors):</strong>
                    <ul className="list-disc pl-5 mt-1.5 space-y-1 text-sm">
                      <li>Razorpay — payment processing</li>
                      <li>Hostinger / HostingRaja — hosting and media file storage</li>
                      <li>cPanel SMTP (mail.m4manna.com) — transactional email delivery</li>
                      <li>Emergent — application deployment and infrastructure</li>
                      <li>Google (YouTube IFrame API) — only if you watch an embedded video</li>
                    </ul>
                  </li>
                  <li><strong className="text-white">With your consent:</strong> whenever you choose to connect your account to another platform or share content publicly.</li>
                  <li><strong className="text-white">Public content:</strong> your username, display name, uploaded songs, bio links and public profile are visible to anyone who visits your profile.</li>
                  <li><strong className="text-white">Legal:</strong> when required by law, subpoena, or to protect the rights, property, or safety of M4manna, our users, or the public.</li>
                  <li><strong className="text-white">Business transfers:</strong> if M4manna is involved in a merger, acquisition, or asset sale, your data may be transferred — you'll be notified before this happens.</li>
                </ul>
              </Section>

              {/* 6. Facebook */}
              <Section icon={Facebook} title="6. Facebook Platform Data" id="facebook">
                <p>
                  If you choose to use Facebook Login or any Facebook-based feature to access M4manna, we may receive
                  the following information from Facebook, subject to your Facebook privacy settings:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Your public profile (name, Facebook ID, profile picture)</li>
                  <li>Your email address (if you grant that permission)</li>
                  <li>Any additional data you explicitly agree to share</li>
                </ul>
                <p>
                  We use this data only to create and authenticate your M4manna account, pre-fill your profile, and
                  provide you with a smoother sign-in experience. We do <strong className="text-white">not</strong>{' '}
                  post to Facebook on your behalf without a separate explicit action.
                </p>
                <p>
                  You can revoke M4manna's access to your Facebook data at any time by visiting your{' '}
                  <a
                    href="https://www.facebook.com/settings?tab=applications"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFE500] hover:underline"
                  >
                    Facebook Apps and Websites settings
                  </a>. Revoking access does not automatically delete your M4manna account — see Section 10 for
                  account deletion.
                </p>
              </Section>

              {/* 7. Cookies */}
              <Section icon={Database} title="7. Cookies & Tracking" id="cookies">
                <p>We use the following types of client-side storage:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Essential:</strong> JWT auth tokens and session cookies to keep you logged in. The Service cannot function without these.</li>
                  <li><strong className="text-white">Preferences:</strong> theme, volume, last-played song — stored in your browser's local storage.</li>
                  <li><strong className="text-white">Analytics (if enabled):</strong> aggregated, anonymized usage metrics to understand feature adoption.</li>
                </ul>
                <p>
                  We do <strong className="text-white">not</strong> use advertising cookies or cross-site tracking
                  pixels. You can clear cookies and local storage at any time via your browser settings.
                </p>
              </Section>

              {/* 8. Retention */}
              <Section icon={Clock} title="8. Data Retention" id="retention">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Active accounts:</strong> we retain your data for as long as your account is active.</li>
                  <li><strong className="text-white">Deleted accounts:</strong> account and uploaded content are permanently deleted within <strong className="text-white">30 days</strong> of your deletion request, except items we must keep for legal or financial reasons (e.g. tax records for completed transactions, which we may retain up to 7 years).</li>
                  <li><strong className="text-white">Logs:</strong> server and security logs are retained for up to 90 days unless they are part of an active investigation.</li>
                  <li><strong className="text-white">Backups:</strong> deleted data may persist in encrypted backups for up to 30 days before being purged.</li>
                </ul>
              </Section>

              {/* 9. Your Rights */}
              <Section icon={Shield} title="9. Your Rights & Choices" id="your-rights">
                <p>Depending on where you live, you have the right to:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-white">Access</strong> the personal data we hold about you.</li>
                  <li><strong className="text-white">Correct</strong> inaccurate or incomplete information (from your account Settings).</li>
                  <li><strong className="text-white">Delete</strong> your account and associated personal data (see Section 10).</li>
                  <li><strong className="text-white">Export</strong> a machine-readable copy of your data (data portability).</li>
                  <li><strong className="text-white">Object</strong> to or restrict certain processing.</li>
                  <li><strong className="text-white">Withdraw consent</strong> at any time for processing based on consent.</li>
                  <li><strong className="text-white">Lodge a complaint</strong> with your local data-protection authority.</li>
                </ul>
                <p>
                  To exercise any of these rights, email{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFE500] hover:underline">{CONTACT_EMAIL}</a>. We
                  respond to verified requests within 30 days.
                </p>
              </Section>

              {/* 10. Delete */}
              <Section icon={UserX} title="10. How to Delete Your Data" id="delete-data">
                <p>You can delete your M4manna account and all associated personal data in two ways:</p>
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>
                    <strong className="text-white">Self-service:</strong> go to{' '}
                    <Link to="/settings" className="text-[#FFE500] hover:underline">Settings → Account → Delete My Account</Link>{' '}
                    and confirm. Your account is deactivated immediately and permanently deleted within 30 days.
                  </li>
                  <li>
                    <strong className="text-white">Email request:</strong> send a message from the email on file to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}?subject=Delete%20my%20account`} className="text-[#FFE500] hover:underline">{CONTACT_EMAIL}</a>{' '}
                    with the subject &quot;Delete my account&quot;. We will verify your identity and complete the deletion
                    within 30 days.
                  </li>
                </ol>
                <p className="mt-3">
                  If you signed up with Facebook Login, you can additionally remove M4manna from your{' '}
                  <a
                    href="https://www.facebook.com/settings?tab=applications"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFE500] hover:underline"
                  >
                    Facebook Apps settings
                  </a>. We will honor Facebook data-deletion callbacks for any Facebook-sourced data we hold.
                </p>
              </Section>

              {/* 11. Children */}
              <Section icon={Baby} title="11. Children's Privacy" id="children">
                <p>
                  M4manna is not directed to children under the age of 13 (or the minimum age required in your country).
                  We do not knowingly collect personal information from children. If you are a parent or guardian and
                  believe your child has provided us data, please contact us so we can remove it.
                </p>
              </Section>

              {/* 12. International */}
              <Section icon={Globe} title="12. International Data Transfers" id="international">
                <p>
                  M4manna is operated from India. If you access the Service from outside India, your data may be
                  transferred to, stored, and processed in India or in countries where our service providers operate.
                  Where required, we use appropriate safeguards such as Standard Contractual Clauses.
                </p>
              </Section>

              {/* 13. Changes */}
              <Section icon={AlertTriangle} title="13. Changes to This Policy" id="changes">
                <p>
                  We may update this Privacy Policy occasionally. When we make material changes we will notify you by
                  email and/or a prominent notice on the Service before the change takes effect. The &quot;Effective
                  date&quot; at the top of this page always reflects the latest revision.
                </p>
              </Section>

              {/* 14. Contact */}
              <Section icon={Mail} title="14. Contact Us" id="contact">
                <p>Questions, requests, or complaints? Reach out to us at:</p>
                <div className="bg-[#1E2028] rounded-xl p-5 mt-3 space-y-1">
                  <p><strong className="text-white">M4manna</strong></p>
                  <p>Email (privacy): <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFE500] hover:underline">{CONTACT_EMAIL}</a></p>
                  <p>Email (general support): <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#FFE500] hover:underline">{SUPPORT_EMAIL}</a></p>
                  <p>Website: <a href="https://m4manna.com" className="text-[#FFE500] hover:underline">https://m4manna.com</a></p>
                </div>
              </Section>

              <div className="pt-6 border-t border-[#1E2028] text-sm text-[#666]">
                <p>
                  By continuing to use M4manna, you confirm you have read, understood, and agreed to this Privacy Policy.
                  See also our <Link to="/terms" className="text-[#FFE500] hover:underline">Terms of Service</Link>.
                </p>
              </div>
            </div>

            {/* Table of Contents - sticky sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-[#16181D] border border-[#1E2028] rounded-2xl p-5">
                <h3 className="text-sm uppercase tracking-wider text-[#FFE500] font-bold mb-3">On This Page</h3>
                <nav className="space-y-1" data-testid="privacy-toc">
                  {toc.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-[#A0A0A0] hover:text-white hover:bg-[#1E2028] px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
